import React, {useContext, useEffect, useState} from 'react';
import {useParams, useNavigate} from "react-router-dom";
import {corporatePersonnel_getById} from "../../network/api/corporatePersonnel.api";
import {ErrorContext} from "../../components/GympinPagesProvider";
import _UserDelete from "./partials/_UserDelete";
import _UserGroup from "./partials/_UserGroup";
import _UserCredits from "./partials/_UserCredits";
import _UserBaseData from "./partials/_UserBaseData";
import {
    Avatar,
    Box,
    Card,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
    AccountBalanceWalletRounded,
    ArrowForwardRounded,
    GroupWorkRounded,
    PhoneIphoneRounded
} from "@mui/icons-material";
import {decodeId, toPriceWithComma} from "../../helper/utils";

const SingleUser = () => {
    const {PersonnelId} = useParams();
    const navigate = useNavigate();
    const error = useContext(ErrorContext);
    const [corporatePersonnel, setCorporatePersonnel] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getCorporatePerson();
    }, [PersonnelId]);

    function getCorporatePerson() {
        if (!PersonnelId) return;
        setLoading(true);
        corporatePersonnel_getById({id: decodeId(PersonnelId)}).then(result => {
            setCorporatePersonnel(result.data.Data);
            setLoading(false);
        }).catch(e => {
            setLoading(false);
            try {
                error.showError({message: e.response?.data?.Message || "خطا در دریافت مشخصات کارمند"});
            } catch (f) {
                error.showError({message: "خطای نامشخص"});
            }
        });
    }

    const user = corporatePersonnel?.User;
    const fullName = user?.FullName || "بدون نام";
    const phone = user?.PhoneNumber || corporatePersonnel?.PhoneNumber;
    const totalCredit = corporatePersonnel?.TotalCredit || 0;
    const groupName = corporatePersonnel?.PersonnelGroup?.Name;

    return (
        <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
            <title>{fullName ? `${fullName} | پرونده پرسنل` : "مشخصات کارمند"}</title>

            {/* Back Button & Top Profile Banner */}
            <Box sx={{ mb: 3.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Tooltip title="بازگشت به لیست پرسنل">
                        <IconButton
                            onClick={() => navigate("/personnel")}
                            sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                        >
                            <ArrowForwardRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        مدیریت پرسنل / جزئیات پرونده کارمند
                    </Typography>
                </Box>

                {/* Profile Hero Card */}
                <Card
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 14px rgba(0, 0, 0, 0.03)',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between',
                        gap: 2.5
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        <Avatar
                            src={user?.Avatar?.Url}
                            alt={fullName}
                            sx={{
                                width: { xs: 58, sm: 70 },
                                height: { xs: 58, sm: 70 },
                                borderRadius: 3.5,
                                bgcolor: 'primary.main',
                                fontSize: '1.4rem',
                                fontWeight: 800,
                                boxShadow: '0 4px 14px rgba(28, 139, 156, 0.25)',
                                border: '2px solid',
                                borderColor: 'background.paper'
                            }}
                        >
                            {fullName.slice(0, 2)}
                        </Avatar>

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap', mb: 0.6 }}>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: 'text.primary', fontSize: { xs: '1.2rem', sm: '1.45rem' } }}>
                                    {fullName}
                                </Typography>
                                {groupName && (
                                    <Chip
                                        icon={<GroupWorkRounded sx={{ fontSize: 16 }} />}
                                        label={groupName}
                                        size="small"
                                        sx={{
                                            fontWeight: 700,
                                            borderRadius: 2,
                                            bgcolor: 'rgba(28, 139, 156, 0.08)',
                                            color: 'primary.main',
                                            border: '1px solid',
                                            borderColor: 'rgba(28, 139, 156, 0.2)'
                                        }}
                                    />
                                )}
                            </Box>

                            {phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'text.secondary' }}>
                                    <PhoneIphoneRounded sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ dir: 'ltr', letterSpacing: 0.5, fontWeight: 600 }}>
                                        {phone}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Balance Chip / Metric */}
                    <Box
                        sx={{
                            p: 2,
                            px: 3,
                            borderRadius: 3.5,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(28, 139, 156, 0.12)' : 'rgba(28, 139, 156, 0.06)',
                            border: '1px solid',
                            borderColor: 'rgba(28, 139, 156, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            alignSelf: { xs: 'stretch', md: 'auto' }
                        }}
                    >
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 3,
                                bgcolor: 'primary.main',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(28, 139, 156, 0.3)'
                            }}
                        >
                            <AccountBalanceWalletRounded />
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                مجموع اعتبار فعال پرسنل
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                {toPriceWithComma(totalCredit)}
                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    تومان
                                </Typography>
                            </Typography>
                        </Box>
                    </Box>
                </Card>
            </Box>

            {/* Main Content Layout */}
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                    <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main', mb: 2 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        در حال بارگذاری اطلاعات پرونده کارمند...
                    </Typography>
                </Box>
            ) : corporatePersonnel ? (
                <Grid container spacing={3}>
                    {/* Left Column: Personnel Settings & Danger Zone */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <_UserBaseData
                                corporatePersonnel={corporatePersonnel}
                                getCorporatePerson={getCorporatePerson}
                            />
                            <_UserGroup
                                corporatePersonnel={corporatePersonnel}
                                updatePage={getCorporatePerson}
                            />
                            <_UserDelete
                                corporatePersonnel={corporatePersonnel}
                            />
                        </Box>
                    </Grid>

                    {/* Right Column: Credits and Transactions Timeline */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <_UserCredits
                            corporatePersonnel={corporatePersonnel}
                            updatePage={getCorporatePerson}
                        />
                    </Grid>
                </Grid>
            ) : null}
        </Container>
    );
};

export default SingleUser;

